import { Tooltip } from '@livecontrol/core-ui';
import styled from 'styled-components';
import type { ScheduledEvent } from '../../graphql';
import { AudienceIcon, CloseUpsIcon, EndEventIcon, IntroOutroIcon, MontageIcon, QuickShotsIcon, WideShotsIcon } from './icons';

interface PreferencesModel {
    preferences: {
        quickShots: {
            option: number;
        };
        closeUpShots: {
            option: number;
        };
        wideVenueShots: {
            option: number;
        };
        audienceShots: {
            option: number;
        };
        detailedViewShots: {
            option: number;
        };
        eventEnding: {
            option: number;
        };
    };
    slidesEnabled: boolean;
    montageEnabled: boolean;
}

const Wrapper = styled.div`
    .bg-danger {
        background-color: #861515!important;
    }
`;

export const Preferences = ({ event }: { event: Partial<PreferencesModel> & ScheduledEvent }): React.ReactElement => (
    <Wrapper className="d-flex flex-column gap-12px">
        <div className="text-white font-weight-bold">Tool Box</div>
        <Tooltip tip={<div>
            {event.preferences?.quickShots.option === 1
            ? 'Use Quickshots & live joystick for moments and effects when it makes sense with the action.'
            : 'Do not use Quick Shots or any live movements during the event.'}
        </div>}>
            <div className={`rounded p-12px text-white text-nowrap ${event.preferences?.quickShots.option === 1 ? 'bg-success' : 'bg-danger'}`}>
                <QuickShotsIcon className="mr-12px" />
                {event.preferences?.quickShots.option === 1 ? 'Quick Shots' : 'No Quick Shots'}
            </div>
        </Tooltip>
        {event.slidesEnabled && (
            <Tooltip tip={<div>Enable the Intro Slide prior to going live and the Outro slide prior to ending the event to ensure the first and last frames are slides.</div>}>
            <div className="rounded bg-success p-12px text-white text-nowrap">
                <IntroOutroIcon className="mr-12px" />
                Intro/Outro Slides Enabled</div>
            </Tooltip>
        )}
        {event.montageEnabled && (
            <Tooltip tip={<div>Use Full Screen graphics to show the video montage or any graphics.  Do not capture the screen or monitor on-site.</div>}>
                <div className="rounded bg-success p-12px text-white text-nowrap">
                    <MontageIcon className="mr-12px" />
                    Montage Integration
                </div>
            </Tooltip>
        )}

        <div className="text-white font-weight-bold">Framing</div>
        <Tooltip tip={event.preferences?.closeUpShots.option === 1
        ? 'Capture close-ups of individuals or action.  Include mid and wide shots but primarily use close-ups when possible.'
        : 'Do not capture close-ups.  Stick to medium and wide shots.'}>
            <div className={`rounded p-12px text-white text-nowrap ${event.preferences?.closeUpShots.option === 1 ? 'bg-success' : 'bg-danger'}`}>
                <CloseUpsIcon className="mr-12px" />
                {event.preferences?.closeUpShots.option === 1 ? 'Close ups' : 'No close ups'}
            </div>
        </Tooltip>
        <Tooltip tip={event.preferences?.wideVenueShots.option === 1
            ? 'Capture wide shots when possible to show group action and the venue.'
            : 'Do not capture wide shots.  Stick to medium and close-ups to capture the action.'
        }>
            <div className={`rounded p-12px text-white text-nowrap ${event.preferences?.wideVenueShots.option === 1 ? 'bg-success' : 'bg-danger'}`}>
                <WideShotsIcon className="mr-12px" />
                {event.preferences?.wideVenueShots.option === 1 ? 'Wide shots' : 'No Wide shots'}
            </div>
        </Tooltip>

        {event.preferences?.audienceShots && (<Tooltip tip={<div>
        {event.preferences.audienceShots.option === 1 ? 'Show the audience or congregation when appropriate.  Do not show empty seats.' : null}
        {event.preferences.audienceShots.option === 2 ? 'Only show the audience when required or specified by a cue sheet or other note.  Avoid showing the audience when a close-up or medium shot can be used.' : null}
        {event.preferences.audienceShots.option === 3 ? 'Do not show the audience whenever possible.  If necessary, frame as little of the audience as possible when needed to capture action.  Do not show cut-away shots to the audience ever.' : null}
        </div>
        }>
            <div className={`rounded p-12px text-white text-nowrap ${event.preferences.audienceShots.option === 1 ? 'bg-success' : event.preferences.audienceShots.option === 2 ? 'bg-warning text-beta' : 'bg-danger'}`}>
                <AudienceIcon className="mr-12px" />
                {event.preferences.audienceShots.option === 1 ? 'Audience shots' : ''}
                {event.preferences.audienceShots.option === 2 ? 'Show audience if required' : ''}
                {event.preferences.audienceShots.option === 3 ? 'No Audience shots' : ''}
            </div>
        </Tooltip>)}

        <Tooltip tip={<div>{event.preferences?.detailedViewShots.option === 1
        ? 'Capture B-roll including details of the venue - stained glass, paintings, flowers, architecture - anything that would add to the interest of the footage.'
        : 'Avoid using detailed venue shots or "B-roll" during the event. Keep the focus on the action and/or area the action is taking place. '
        }</div>}>
            <div className={`rounded p-12px text-white text-nowrap ${event.preferences?.detailedViewShots.option === 1 ? 'bg-success' : 'bg-danger'}`}>
                <i className="mr-12px fas fa-search-plus" />
                {event.preferences?.detailedViewShots.option === 1 ? 'Detailed venue shots' : 'No Detailed venue shots'}
            </div>
        </Tooltip>

        {event.preferences?.eventEnding && (
        <>
        <div className="text-white font-weight-bold">Policy</div>
        <Tooltip tip={<div>
        {event.preferences.eventEnding.option === 1 ? 'End the event when you know for certain it is over. Reach out to your Event Support team if you are unsure.' : null}
        {event.preferences.eventEnding.option === 3 ? 'Wait for the spoken cue to end the event.  This will be given by someone on-site as part of the event.  Otherwise, do not end.' : null}
        {event.preferences.eventEnding.option === 2 ? 'Do not end the event.  The client will end the event in their dashboard.  If all action/sound has stopped, reach out to Support for confirmation before ending.' : null}
        </div>}>
            <div className={`rounded p-12px text-white text-nowrap ${event.preferences.eventEnding.option === 2 ? 'bg-danger' : 'bg-success'}`}>
                <EndEventIcon className="mr-12px" />
                {event.preferences.eventEnding.option === 1 ? 'End Event: Your Choice' : ''}
                {event.preferences.eventEnding.option === 3 ? 'End Event: Spoken Cue' : ''}
                {event.preferences.eventEnding.option === 2 ? 'End Event: Client Will End' : ''}
            </div>
        </Tooltip>
            </>
        )}
    </Wrapper>
);
